import React, { createContext, useEffect, ReactNode, useState } from 'react'

import axios from 'axios'

// WASM 모듈 import
import { initializeWasm, getInitializeWasmStatus } from '../wasm/wasmutils';
import { get_server_network_info } from '../wasm/rust_wassm_fetch';

// import jsonOtherTokenData from '../data/otherTokenData.json';
// import jsonNetworkData from '../data/networkData.json';

export interface NetworkInfo {
    name: string;
    symbol: string;
    description: string;
    networkid: string;
    chainid: string;
    currency: string;
    group: string;
    rpc_http: string;
    rpc_ws: string;
    scan: string;
    blockexp: string;
    icon: string;
		is_active: boolean;
}

export interface TokenInfo {
    name: string;
    symbol: string;
    description: string;
    type: string;
    contract: string;
    decimal: string;
    icon: string;
    networkid: string;
    chainid: string;
    networkname: string;
    useswap: boolean;
}

interface DefaultTokenContextProps {
	networkInfo: NetworkInfo[],
	tokenInfo: TokenInfo[],
	userTokenInfo: TokenInfo[],
	getNetworkToken: (network:string) => Promise<TokenInfo[]>,
	getNetworkInfo: (network:string) => Promise<NetworkInfo[]>,
	searchToken: (symbol: string) => TokenInfo | null,
	searchTokenContract: (contract: string) => Promise<TokenInfo | null>,
	searchNetwork: (symbol: string) => NetworkInfo | null,
	addNewToken: (newToken: TokenInfo) => Promise<string>,
	removeToken: (contractAddress: string) => Promise<string>,
	forceTokenDataUpdate: () => Promise<void>,
}

const defaultContext: DefaultTokenContextProps = {
	networkInfo: [],
	tokenInfo: [],
	userTokenInfo: [],
	getNetworkToken: () => Promise.resolve([]),
	getNetworkInfo: () => Promise.resolve([]),
	searchToken: () => null,
	searchTokenContract: () => Promise.resolve(null),
	searchNetwork: () => null,
	addNewToken: () => Promise.resolve(''),
	removeToken: () => Promise.resolve(''),
	forceTokenDataUpdate: () => Promise.resolve(),
}

export const DefaultTokenContext = createContext<DefaultTokenContextProps>(defaultContext)

export const DefaultTokenProvider = ({ children }: { children: ReactNode }) => {
	const [networkInfo, setNetworkInfo] = useState<NetworkInfo[]>([]);
	const [tokenInfo, setTokenInfo] = useState<TokenInfo[]>([]);
	const [userTokenInfo, setUserTokenInfo] = useState<TokenInfo[]>([]);

	useEffect(() => {
		const initData = async () => {
			try{
				// wasm init
				console.warn(`DefaultTokenContext 웹 어셈블리 init`)
				await initializeWasm();

			} catch(error) {
				console.error(`DefaultTokenContext loadSetup error : `, error)
			}
		}

		const initializeData = async () => {
			// console.log(`DefaultTokenContext initializeData`)
			const fetchedNetworkData = await getDefaultNetwork();
			const fetchedTokenData = await getDefaultToken();
			const fetchedUserTokenData = await getUserToken();

			if (fetchedNetworkData) {
					// console.log(`initializeData fetchedNetworkData`, fetchedNetworkData)
					const parsedNetworkData = JSON.parse(fetchedNetworkData);
					const networkArray = Object.keys(parsedNetworkData).map(key => parsedNetworkData[key]);
					setNetworkInfo(networkArray);
			}
			if (fetchedTokenData) {
					// console.log(`initializeData fetchedTokenData`)
					const parsedTokenData = JSON.parse(fetchedTokenData);
					const tokenArray = Object.keys(parsedTokenData).map(key => parsedTokenData[key]);
					setTokenInfo(tokenArray);
			}
			if (fetchedUserTokenData) {
					// console.log(`initializeData fetchedUserTokenData`)
					const parsedUserTokenData = JSON.parse(fetchedUserTokenData);
					const userTokenArray = Object.keys(parsedUserTokenData).map(key => parsedUserTokenData[key]);
					setUserTokenInfo(userTokenArray);
			}
		};

		initData()

		initializeData()

	}, [])

	const fetchData = 
	async (
		fallbackData: any, 
		storageKey: string, 
		forceUpdate:boolean=false
	): Promise<any> => {
		try {
				// 로컬 스토리지에 저장된 기본 데이터 있는지 확인
				const storedData = localStorage.getItem(storageKey)
				
				if (!storedData || forceUpdate) {
						// 로컬 스토리지에 저장된 기본 데이터가 없으면 json 데이터를 localstorage 에 저장
						if(null != fallbackData) {
								localStorage.setItem(storageKey, JSON.stringify(fallbackData));
						}

						const savedData = localStorage.getItem(storageKey)

						// console.log(`set DefaultData success ${storageKey} | ${savedData}`);
						return savedData;
				} else {
						// 로컬 스토리지에 저장된 데이터 반환
						const savedData = localStorage.getItem(storageKey)
						return savedData;
				}
		} catch (error) {
				console.error(`Fetching ${storageKey} failed:`, error);
				return null;
		}
	};

	const fetchNetworkInfoFromServer = 
	async (
		storageKey: string, 
		network: string, 
		forceUpdate:boolean=false
	): Promise<any> => {
		try {
			// 로컬 스토리지에 저장된 기본 데이터 있는지 확인
			const storedData = localStorage.getItem(storageKey)
			
			if (!storedData || true === forceUpdate) {
					// REACT_APP_QWALLET_SERVER_URLv1/tx/networkinfo 엔드포인트에 네트워크 정보를 요청
					// const response = await axios.post(`${process.env.REACT_APP_QWALLET_SERVER_URL}/v1/walletsetup/networkinfo`, {
					// 		network: network,
					// });
					const url = process.env.REACT_APP_QWALLET_SERVER_URL?.toString() || ''
					console.log(`DefaultTokenContext fetchNetworkInfoFromServer start ${url} | ${network}`)

					// wasm init 상태 확인
					const inited = await getInitializeWasmStatus();
					console.log(`DefaultTokenContext fetchNetworkInfoFromServer Wasm inited`, inited)
					if(false === inited) {
						return null;
					}

					const res = await get_server_network_info(url, network.toLowerCase());
					const response = JSON.parse(res);
					console.log(`DefaultTokenContext fetchNetworkInfoFromServer response`, response)

					if (true === response.result && response.data) {
							const serverData = response.data;

							const networkData = serverData.map((network: any) => ({
									name: network.ni_name?.toLowerCase(),
									symbol: network.ni_symbol?.toLowerCase(),
									description: network.ni_description,
									currency: network.ni_currency,
									group: network.ni_network_group,
									networkid: network.ni_network_id,
									chainid: network.ni_chain_id,
									rpc_http: network.ni_rpc_api_http_url,
									rpc_ws: network.ni_rpc_api_ws_url,
									scan: network.ni_scan_api_url,
									blockexp: network.ni_blockexp_url,
									icon: network.ni_icon,
									is_active: network.ni_is_active
							}));
							localStorage.setItem(storageKey, JSON.stringify(networkData));
					}

					const savedData = localStorage.getItem(storageKey)

					// console.log(`set DefaultData success ${storageKey} | ${savedData}`);
					return savedData;
			} else {
					// 로컬 스토리지에 저장된 데이터 반환
					const savedData = localStorage.getItem(storageKey)
					return savedData;
			}
		} catch (error) {
				console.error(`Fetching Network Info ${storageKey} failed:`, error);
				alert('An issue occurred while fetching the network info');
				return null;
		}
	};

	/**
	 * 지갑 서버에 토큰 정보 요청
	 * @param storageKey 
	 * @param network 
	 * @param forceUpdate 
	 * @returns 
	 */
	const  fetchTokenInfoFromServer = 
	async (
		storageKey: string, 
		network: string, 
		forceUpdate:boolean=false
	): Promise<any> => {
		try {
				// 로컬 스토리지에 저장된 기본 데이터 있는지 확인
				const storedData = localStorage.getItem(storageKey)
				
				if (!storedData || forceUpdate) {
						// REACT_APP_QWALLET_SERVER_URLv1/tx/tokeninfo 엔드포인트에 토큰 정보를 요청
						const response = await axios.post(`${process.env.REACT_APP_QWALLET_SERVER_URL}/v1/tx/tokeninfo`, {
								network: network,
						});
						console.log(`DefaultTokenContext fetchTokenInfoFromServer response`, response.data)

						if (true === response.data.result && response.data.data) {
								const serverData = response.data.data;
								const tokenData = serverData.map((token: any) => ({
										name: token.ti_name?.toLowerCase(),
										symbol: token.ti_symbol?.toLowerCase(),
										description: token.ti_description,
										type: token.ti_type?.toLowerCase(),
										contract: token.ti_mintaddress,
										decimal: token.ti_decimal,
										icon: `${token.ti_icon}`,
										networkid: token.ti_networkid,
										chainid: token.ti_chainid,
										networkname: token.ti_network?.toLowerCase(),
										useswap: token.ti_useswap
								}));
								localStorage.setItem(storageKey, JSON.stringify(tokenData));
						}

						const savedData = localStorage.getItem(storageKey)

						// console.log(`set DefaultData success ${storageKey} | ${savedData}`);
						return savedData;
				} else {
						// 로컬 스토리지에 저장된 데이터 반환
						const savedData = localStorage.getItem(storageKey)
						return savedData;
				}
		} catch (error) {
				console.error(`Fetching Token Info ${storageKey} failed:`, error);
				alert('An issue occurred while fetching the token info');
				return null;
		}
	};

	/**
	 * 기본 네트워크 정보 요청
	 * @param forceUpdate 
	 * @returns 
	 */
	const getDefaultNetwork = async (forceUpdate:boolean=false): Promise<any> => {
		const networkDataKey = 'networkData';
		console.log(`DefaultTokenContext getDefaultNetwork networkDataKey`, networkDataKey)

		const returnData = await fetchNetworkInfoFromServer(networkDataKey, 'allnetwork', forceUpdate);
		return returnData;
	};

	/**
	 * 기본 토큰 정보 요청
	 * @param forceUpdate 
	 * @returns 
	 */
	const getDefaultToken = async (forceUpdate:boolean=false): Promise<any> => {
			const tokenDataKey = 'tokenData';

			const returnData = await fetchTokenInfoFromServer(tokenDataKey, 'allnetwork', forceUpdate);
			return returnData;
	};

	/**
	 * 네트워크 정보 조회
	 * @param network 
	 * @returns 
	 */
	const getNetworkInfo = async (network: string): Promise<any> => {
		
		// 전체 네트워크 정보에서 해당 네트워크만 필터링
		let data = [];
		if(network) {
			data = networkInfo.filter(temp => temp.symbol.toLowerCase() === network.toLowerCase());	
		} else {
			data = networkInfo;
		}
		
		return data;
	}

	/**
	 * 네트워크 설정 정보 조회
	 * @param symbol 
	 * @returns 
	 */
	const searchNetwork = (symbol: string): NetworkInfo | null => {
		if (!networkInfo){
			console.error(`DefaultTokenContext searchNetwork networkInfo is null`)
			return null;
		}

		return networkInfo.find(network => network.symbol.toLowerCase() === symbol.toLowerCase()) || null;
	};
	
	/**
	 * 저장된 토큰 정보에서 네트워크별 토큰 정보 조회
	 * @param network 
	 * @returns 
	 */
	const getNetworkToken = async (network:string): Promise<any> => {
		// 전체 토큰 정보에서 해당 네트워크의 토큰만 필터링
		const networkTokens = tokenInfo.filter(token => token.networkname.toLowerCase() === network.toLowerCase());
		console.log(`DefaultTokenContext getNetworkToken network:${network} count:${networkTokens.length}`, networkTokens);
		
		return networkTokens;
	};

	const getUserToken = async (): Promise<any> => {
			const tokenDataKey = 'userTokenData';

			const returnData = await fetchData(null, tokenDataKey);
			return returnData;
	};

	/**
	 * 기본 토큰 정보 조회
	 * @param symbol 
	 * @returns 
	 */
	const searchToken = (symbol: string): TokenInfo | null => {
		if (!tokenInfo && ! userTokenInfo) return null;

		const defaultToken = tokenInfo.find(token => token.symbol.toLowerCase() === symbol.toLowerCase()) || null;
		const userToken = userTokenInfo.find(token => token.symbol.toLowerCase() === symbol.toLowerCase()) || null;

		return defaultToken || userToken;
	};

	const searchTokenContract = async (contract: string): Promise<TokenInfo | null> => {
		const fetchedTokenData = await getDefaultToken();
		const fetchedUserTokenData = await getUserToken();

		if (!fetchedTokenData && ! fetchedUserTokenData) {
				console.log(`searchTokenContract tokenInfo userTokenInfo null`)
				return null;
		}
		
		const parsedTokenData = JSON.parse(fetchedTokenData);
		let tokenData = [];
		if(parsedTokenData) {
				tokenData = Object.keys(parsedTokenData).map(key => parsedTokenData[key]);
		}

		const parsedUserTokenData = JSON.parse(fetchedUserTokenData);
		let userTokenData = [];
		if(parsedUserTokenData) {
				userTokenData = Object.keys(parsedUserTokenData).map(key => parsedUserTokenData[key]);
		}

		const defaultToken = tokenData.find((token: TokenInfo)  => token.contract && token.contract === contract) || null;
		const userToken = userTokenData.find((token: TokenInfo)  => token.contract && token.contract === contract) || null;

		return defaultToken || userToken;
	};

	// 새로운 토큰을 추가하는 메소드
	const addNewToken = async (newToken: TokenInfo): Promise<string> => {
		const tokenDataKey = 'userTokenData';
		const currentTokens = userTokenInfo;
		
		// 중복된 토큰 contract 주소가 있는지 확인
		const tokenExists = currentTokens.some(token => token.contract === newToken.contract);
		
		if (!tokenExists) {
				const updatedTokens = [...currentTokens, newToken];            

				localStorage.setItem(tokenDataKey, JSON.stringify(updatedTokens));

				setTokenInfo(updatedTokens);

				console.log(`New token added to ${tokenDataKey}`);
				return 'success';
		} else {
				return 'overlap';
		}
	};

	// 토큰을 삭제하는 메소드
	const removeToken = async (contractAddress: string): Promise<string> => {
		const tokenDataKey = 'userTokenData';
		const currentTokens = userTokenInfo;

		// 해당 contract 주소를 가진 토큰 찾기
		const updatedTokens = currentTokens.filter(token => token.contract !== contractAddress);

		// 업데이트된 토큰 리스트를 로컬 스토리지에 저장
		localStorage.setItem(tokenDataKey, JSON.stringify(updatedTokens));

		// 상태 업데이트
		setUserTokenInfo(updatedTokens);

		console.log(`Token with contract address ${contractAddress} removed from ${tokenDataKey}`);
		return 'success';
	};

	 // 강제 업데이트
	const forceTokenDataUpdate = async (): Promise<void> => {
		console.log(`DefaultTokenContext forceTokenDataUpdate start`)

		// wasm init 확인
		await initializeWasm();

		const fetchedNetworkData = await getDefaultNetwork(true);
		const fetchedTokenData = await getDefaultToken(true);
		const fetchedUserTokenData = await getUserToken();

		if (fetchedNetworkData) {
				console.log(`DefaultTokenContext forceTokenDataUpdate fetchedNetworkData`, fetchedNetworkData)
				const parsedNetworkData = JSON.parse(fetchedNetworkData);
				const networkArray = Object.keys(parsedNetworkData).map(key => parsedNetworkData[key]);
				setNetworkInfo(networkArray);
		}
		if (fetchedTokenData) {
				// console.log(`forceTokenDataUpdate fetchedTokenData`, fetchedTokenData)
				const parsedTokenData = JSON.parse(fetchedTokenData);
				const tokenArray = Object.keys(parsedTokenData).map(key => parsedTokenData[key]);
				setTokenInfo(tokenArray);
		}
		if (fetchedUserTokenData) {
				// console.log(`forceTokenDataUpdate fetchedUserTokenData`, fetchedUserTokenData)
				const parsedUserTokenData = JSON.parse(fetchedUserTokenData);
				const userTokenArray = Object.keys(parsedUserTokenData).map(key => parsedUserTokenData[key]);
				setUserTokenInfo(userTokenArray);
		}
	};
	




	return <DefaultTokenContext.Provider value={{ 
		networkInfo, 
		tokenInfo, 
		userTokenInfo, 
		getNetworkToken,
		getNetworkInfo,
		searchToken, 
		searchTokenContract, 
		searchNetwork, 
		addNewToken, 
		removeToken, 
		forceTokenDataUpdate 
	}}>{children}</DefaultTokenContext.Provider>
}
